import React, {
  useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { Calendar } from 'primereact/calendar';
import { useTranslation } from 'react-i18next';
import { LiliButton, LiliButtonType } from '@lili/lili-frontend-common';
import { useDates } from '../../hooks/useDates';
import { PeriodPresetList } from './PeriodPresetList';
import { StartDatePresentor } from './StartDatePresentor';
import { EndDatePresentor } from './EndDatePresentor';
import Heading from '../../../../generic-components/Heading';
import { DATES_PERIOD_PRESETS, STYLES } from '../../consts';
import { useStore } from '../../../../store';
import { FlowContext } from '../TransactionsWrapper';
import { CloseButton, CloseButtonColor, CloseButtonSize } from '../../../../DesignSystem/LiliCloseButton/CloseButton';

interface IProps {
  closeFilter: () => void;
}
export const FilterDatePickerWrapper = ({ closeFilter }: IProps) => {
  const filterObject = useStore(state => state.filterObject);
  const { customer: { tcTime } } = useStore(state => state.summary);
  const setFilterObject = useStore(state => state.setFilterObject);
  const [selectedPeriod, setSelectedPeriod] = useState<string>(filterObject.datePreset || '');
  const { getPeriodStartAndEndDates } = useDates();
  const ctx = useContext(FlowContext);
  const elFilter = useRef<HTMLDivElement>(null);
  const [localRange, setLocalRange] = useState<Date[] | null>((filterObject.endDate && filterObject.startDate) ? [filterObject.startDate, filterObject.endDate] : null);
  const { t } = useTranslation();

  const isSomeFieldDirty = useMemo(() => [(localRange?.[0] && localRange?.[1]), selectedPeriod].some(i => i), [localRange, selectedPeriod]);

  function handleApply() {
    if (selectedPeriod && selectedPeriod !== 'custom') {
      const { start, end } = getPeriodStartAndEndDates(new Date(), selectedPeriod, tcTime);
      ctx.handleFilterPeriod({ startDate: start, endDate: end, presetType: selectedPeriod });
      setFilterObject({
        ...filterObject, startDate: start, endDate: end, datePreset: selectedPeriod,
      });
      closeFilter();
    } else if (localRange?.[0] && localRange?.[1]) {
      ctx.handleFilterPeriod({ startDate: localRange[0], endDate: localRange[1], presetType: 'custom' });
      setFilterObject({
        ...filterObject, startDate: localRange[0], endDate: localRange[1], datePreset: 'custom',
      });
      closeFilter();
    }
  }

  function handleClear() {
    const { start, end } = getPeriodStartAndEndDates(new Date(), 'any', tcTime);
    ctx.handleFilterPeriod({ startDate: start, endDate: end, presetType: 'any' });
    setFilterObject({
      ...filterObject, startDate: start, endDate: end, datePreset: 'any',
    });
    closeFilter();
  }

  const handleCalendarChange = (e: any) => {
    setSelectedPeriod('custom');
    setLocalRange(e.value);
  };

  // ** Once user selected Preset period, clean the custom period */
  useEffect(() => {
    if (selectedPeriod && selectedPeriod !== 'custom') {
      const { start, end } = getPeriodStartAndEndDates(new Date(), selectedPeriod, tcTime);
      setLocalRange([start, end]);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    window.addEventListener('click', (e) => {
      const elTriggerWrapper = document.getElementById('periodTriggerWrapper');
      // @ts-ignore
      if (elFilter.current && !elFilter?.current?.contains(e.target) && !elTriggerWrapper?.contains(e.target)) {
        closeFilter();
      }
    });
    return () => {
      window.removeEventListener('click', () => {});
    };
  }, []);

  return (
    <div ref={elFilter} className={STYLES.FLOAT_DATE_PICKER.WRAPPER}>
      <CloseButton onClick={closeFilter} id='closePeriodPopUp' color={CloseButtonColor.Gray} size={CloseButtonSize.Medium} className={STYLES.FLOAT_DATE_PICKER.CLOSE_BTN} />
      <div className={STYLES.FLOAT_DATE_PICKER.CONTENT_CONTAINER}>
        <PeriodPresetList periodPresets={DATES_PERIOD_PRESETS} selectedPeriod={selectedPeriod} handlePeriodSelection={(type) => setSelectedPeriod(type)} />
        <div className={STYLES.FLOAT_DATE_PICKER.RIGHT_CONTAINER}>
          <div className={STYLES.FLOAT_DATE_PICKER.CALENDAR_WRAPPER}>
            <div className={STYLES.FLOAT_DATE_PICKER.SELECTOR_INPUT_WRAPPER}>
              <StartDatePresentor startDate={localRange?.[0]} />
              <Heading title='-' className={STYLES.FLOAT_DATE_PICKER.SEPARATOR} />
              <EndDatePresentor endDate={localRange?.[1]} />
            </div>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
            <div onClick={(e) => e.stopPropagation()} className='card flex justify-content-center'>
              {/** @ts-ignore */}
              <Calendar viewDate={localRange?.[1] || null} minDate={new Date(tcTime)} id='PrimeCalendar' inline selectionMode='range' value={localRange} onChange={handleCalendarChange} numberOfMonths={2} />
            </div>
          </div>
          <div className='w-full flex justify-end gap-[14px]'>
            <LiliButton type={LiliButtonType.SECONDARY} onClick={() => handleClear()} title={t('TRANSACTIONS_FACELIFT.DATE_PICKER_WRAPPER.CLEAR')} className={STYLES.CLEAR_BUTTON} id='clear' />
            <LiliButton disabled={!isSomeFieldDirty} onClick={() => handleApply()} title={t('TRANSACTIONS_FACELIFT.DATE_PICKER_WRAPPER.APPLY')} className={STYLES.APPLY_BUTTON} id='apply' />
          </div>
        </div>
      </div>
    </div>
  );
};
